import React from 'react';
import { useValueChange } from '@leverege/ui-hooks';
import { NumericInput, Pane, PropertyGrid, Checkbox, TabPane, TextInput, Text } from '@leverege/ui-elements';
import { ModelTypeSelector } from '@leverege/ui-plugin';
import { Attributes } from '@leverege/ui-attributes';
import { ns } from '@leverege/i18n';
import { PieChartModel } from "../models/index.js";
import PieRolloverEditor from "./PieRolloverEditor.js";
import ValueRangesEditor from "./valueRange/ValueRangesEditor.js";
import ModelUtil from "../models/Util.js";
import S from './PieChartModelEditor.css';
const {
  t
} = ns('ui-attributes-graphs.editors.PieChartModelEditor');
export default function PieChartModelEditor(props) {
  const {
    value,
    objectType
  } = props;
  const onModelChange = useValueChange(PieChartModel, props);
  const labelAligned = PieChartModel.getLabelAlign(value);
  const isAgg = objectType === 'aggResult';
  const attributeOptions = ModelUtil.getAttributesForPie(Attributes.getAttributesFor(objectType));
  return /*#__PURE__*/React.createElement(TabPane, {
    style: {
      height: '100%'
    }
  }, /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "chart",
    tabName: t('chart')
  }, /*#__PURE__*/React.createElement(Pane, {
    variant: "xyChartEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid, {
    columns: 2,
    variant: "xyChartEditor|editor",
    inline: true
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('pieChart')
  }), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    fillCell: true,
    label: t('attribute'),
    postLabel: /*#__PURE__*/React.createElement(Text, {
      className: S.attributeLabel
    }, isAgg ? 'Aggregation' : PieChartModel.getAttribute(value)?.name)
  }, !isAgg && /*#__PURE__*/React.createElement(ModelTypeSelector, {
    values: attributeOptions,
    value: PieChartModel.getAttribute(value) || attributeOptions[0],
    eventData: "setAttribute",
    onChange: onModelChange,
    allowNone: false,
    typeKey: "name",
    isPropertyGrid: true,
    selectorFirst: true
  })), /*#__PURE__*/React.createElement(PropertyGrid.Divider, null), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('limit'),
    fillcell: true
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getLimit(value),
    eventData: "setLimit",
    onChange: onModelChange,
    hint: t('limitHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    fillcell: true,
    label: t('includeNull')
  }, /*#__PURE__*/React.createElement(Checkbox, {
    label: t('includeNullHint'),
    value: PieChartModel.getIncludeNull(value),
    eventData: "setIncludeNull",
    onChange: onModelChange,
    labelVariant: "inputCaption"
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('topMargin')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getTopMargin(value),
    eventData: "setTopMargin",
    onChange: onModelChange,
    hint: t('topMarginHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('rightMargin')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getRightMargin(value),
    eventData: "setRightMargin",
    onChange: onModelChange,
    hint: t('rightMarginHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('bottomMargin')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getBottomMargin(value),
    eventData: "setBottomMargin",
    onChange: onModelChange,
    hint: t('bottomMarginHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('leftMargin')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getLeftMargin(value),
    eventData: "setLeftMargin",
    onChange: onModelChange,
    hint: t('leftMarginHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('size'),
    fillcell: true
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getSize(value),
    eventData: "setSize",
    onChange: onModelChange,
    float: true,
    min: 0,
    max: 125,
    hint: t('sizeHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('donutSize'),
    fillcell: true
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getDonutSize(value),
    eventData: "setDonutSize",
    onChange: onModelChange,
    float: true,
    min: 0,
    max: 150,
    hint: t('donutSizeHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('cornerRadius'),
    fillcell: true
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getCornerRadius(value),
    eventData: "setCornerRadius",
    onChange: onModelChange,
    hint: t('cornerRadiusHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('padAngle'),
    fillcell: true
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getPadAngle(value),
    eventData: "setPadAngle",
    onChange: onModelChange,
    float: true,
    hint: t('padAngleHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('strokeColor'),
    fillcell: true
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getStrokeColor(value),
    eventData: "setStrokeColor",
    onChange: onModelChange,
    hint: t('strokeColorHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('strokeWidth')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getStrokeWidth(value),
    eventData: "setStrokeWidth",
    onChange: onModelChange,
    hint: t('strokeWidthHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('dataColorTheme')
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getDataColorTheme(value),
    eventData: "setDataColorTheme",
    onChange: onModelChange,
    hint: t('dataColorThemeHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('sortDataValues')
  }, /*#__PURE__*/React.createElement(Checkbox, {
    value: PieChartModel.getSortValues(value),
    eventData: "setSortValues",
    onChange: onModelChange
  }))), /*#__PURE__*/React.createElement(PropertyGrid, {
    variant: "xyChartEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('background')
  }), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('backgroundColor')
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getBackgroundColor(value),
    eventData: "setBackgroundColor",
    onChange: onModelChange,
    hint: t('backgroundColorHint')
  }))))), !isAgg && /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "valueRanges",
    tabName: t('valueRanges'),
    contentClassName: S.editor
  }, /*#__PURE__*/React.createElement(ValueRangesEditor, {
    value: value,
    onChange: onModelChange,
    eventData: "setValueRanges",
    objectType: objectType
  })), /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "label",
    tabName: t('labels')
  }, /*#__PURE__*/React.createElement(Pane, {
    variant: "xyChartEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid, {
    columns: 2,
    variant: "xyChartEditor|editor",
    inline: true
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('sliceLabels')
  }), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelsVisible')
  }, /*#__PURE__*/React.createElement(Checkbox, {
    value: PieChartModel.getLabelVisible(value),
    eventData: "setLabelVisible",
    onChange: onModelChange
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelColor'),
    newRow: true
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getLabelColor(value),
    eventData: "setLabelColor",
    onChange: onModelChange,
    hint: t('labelColorHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelFontFamily')
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getLabelFontFamily(value),
    eventData: "setLabelFontFamily",
    onChange: onModelChange,
    hint: t('labelFontFamilyHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelFontSize')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getLabelFontSize(value),
    eventData: "setLabelFontSize",
    onChange: onModelChange,
    hint: t('labelFontSizeHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelXOffset')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getLabelXOffset(value),
    eventData: "setLabelXOffset",
    onChange: onModelChange,
    disabled: labelAligned,
    float: true,
    hint: t('labelXOffsetHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelYOffset')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getLabelYOffset(value),
    eventData: "setLabelYOffset",
    disabled: labelAligned,
    onChange: onModelChange,
    float: true,
    hint: t('labelYOffsetHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelAngle')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getLabelAngle(value),
    eventData: "setLabelAngle",
    disabled: labelAligned,
    onChange: onModelChange,
    hint: t('labelAngle')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelThreshold')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getLabelThreshold(value),
    eventData: "setLabelThreshold",
    float: true,
    onChange: onModelChange,
    hint: t('labelThresholdHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelAlign')
  }, /*#__PURE__*/React.createElement(Checkbox, {
    value: PieChartModel.getLabelAlign(value),
    eventData: "setLabelAlign",
    onChange: onModelChange,
    label: t('labelAlignHint'),
    labelVariant: "inputCaption"
  }))), /*#__PURE__*/React.createElement(PropertyGrid, {
    columns: 2,
    variant: "xyChartEditor|editor",
    inline: true
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('innerCount')
  }), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelVisible')
  }, /*#__PURE__*/React.createElement(Checkbox, {
    value: PieChartModel.getInnerCountVisible(value),
    eventData: "setInnerCountVisible",
    onChange: onModelChange
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('innerCountTotal')
  }, /*#__PURE__*/React.createElement(Checkbox, {
    value: PieChartModel.getInnerCountTotal(value),
    eventData: "setInnerCountTotal",
    onChange: onModelChange
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelColor'),
    newRow: true
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getInnerCountColor(value),
    eventData: "setInnerCountColor",
    onChange: onModelChange,
    hint: t('innerCountColorHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelFontFamily')
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getInnerCountFontFamily(value),
    eventData: "setInnerCountFontFamily",
    onChange: onModelChange,
    hint: t('innerCountFontFamilyHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelFontSize')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getInnerCountFontSize(value),
    eventData: "setInnerCountFontSize",
    onChange: onModelChange,
    hint: t('innerCountFontSizeHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelYOffset')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getInnerCountYOffset(value),
    eventData: "setInnerCountYOffset",
    onChange: onModelChange,
    float: true,
    hint: t('innerCountYOffsetHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Divider, null)), /*#__PURE__*/React.createElement(PropertyGrid, {
    columns: 2,
    variant: "xyChartEditor|editor",
    inline: true
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('innerTitle')
  }), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelVisible')
  }, /*#__PURE__*/React.createElement(Checkbox, {
    value: PieChartModel.getInnerTitleVisible(value),
    eventData: "setInnerTitleVisible",
    onChange: onModelChange
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('innerTitleText'),
    newRow: true
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getInnerTitleText(value),
    eventData: "setInnerTitleText",
    onChange: onModelChange,
    hint: t('innerTitleTextHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelColor')
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getInnerTitleColor(value),
    eventData: "setInnerTitleColor",
    onChange: onModelChange,
    hint: t('innerTitleColorHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelFontFamily')
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getInnerTitleFontFamily(value),
    eventData: "setInnerTitleFontFamily",
    onChange: onModelChange,
    hint: t('innerTitleFontFamilyHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelFontSize')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getInnerTitleFontSize(value),
    eventData: "setInnerTitleFontSize",
    onChange: onModelChange,
    hint: t('innerTitleFontSizeHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('labelYOffset')
  }, /*#__PURE__*/React.createElement(NumericInput, {
    value: PieChartModel.getInnerTitleYOffset(value),
    eventData: "setInnerTitleYOffset",
    onChange: onModelChange,
    float: true,
    hint: t('innerTitleYOffsetHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Divider, null)))), /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "rollover",
    tabName: t('rollover')
  }, /*#__PURE__*/React.createElement(PieRolloverEditor, {
    value: value,
    onChange: onModelChange,
    eventData: "setRollover",
    objectType: objectType
  })), /*#__PURE__*/React.createElement(TabPane.Tab, {
    tabId: "key",
    tabName: t('key'),
    contentClassName: S.editor
  }, /*#__PURE__*/React.createElement(Pane, {
    variant: "xyChartEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid, {
    variant: "xyChartEditor|editor"
  }, /*#__PURE__*/React.createElement(PropertyGrid.Header, {
    title: t('key')
  }), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    fillcell: true,
    label: /*#__PURE__*/React.createElement(Checkbox, {
      label: t('showKey'),
      value: PieChartModel.getKeyVisible(value),
      eventData: "setKeyVisible",
      onChange: onModelChange
    })
  }), /*#__PURE__*/React.createElement(PropertyGrid.Divider, null), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('keyAnimate')
  }, /*#__PURE__*/React.createElement(Checkbox, {
    value: PieChartModel.getKeyAnimate(value),
    eventData: "setKeyAnimate",
    onChange: onModelChange,
    label: t('keyAnimateHint'),
    labelVariant: "inputCaption"
  })), /*#__PURE__*/React.createElement(PropertyGrid.Divider, null), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('keyTitle'),
    newRow: true
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getKeyTitle(value),
    eventData: "setKeyTitle",
    onChange: onModelChange,
    hint: t('keyTitleHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('keyTitleVariant'),
    fillcell: true
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getKeyTitleVariant(value),
    eventData: "setKeyTitleVariant",
    onChange: onModelChange,
    hint: t('keyTitleVariantHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('keyLabelVariant'),
    fillcell: true
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getKeyLabelVariant(value),
    eventData: "setKeyLabelVariant",
    onChange: onModelChange,
    hint: t('keyLabelVariantHint')
  })), /*#__PURE__*/React.createElement(PropertyGrid.Item, {
    label: t('keyCountVariant'),
    fillcell: true
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: PieChartModel.getKeyCountVariant(value),
    eventData: "setKeyCountVariant",
    onChange: onModelChange,
    hint: t('keyCountVariantHint')
  }))))));
}